import type { GameEvent } from '../schema';

export const EDUCATION_EVENTS: GameEvent[] = [
  {
    id: 'education.firstChoice',
    title: 'What now?',
    description:
      'School is finished. The options in front of you cost different amounts of time, money and certainty.',
    category: 'education',
    pools: ['pool.education', 'pool.milestone.launch'],
    stages: ['launch'],
    ageMax: 23,
    weight: 12,
    unique: true,
    priority: 8,
    requires: [{ k: 'educationAtLeast', level: 'highschool' }, { k: 'flag', flag: 'chosePath', value: false }],
    choices: [
      {
        id: 'degree',
        label: 'Go to university',
        detail: 'Four years, real debt, and the widest set of doors afterwards.',
        hint: 'slow',
        tags: ['education', 'patience'],
        outcomes: [
          {
            weight: 1,
            label: 'Enrolled',
            narrative: 'A loan agreement, a reading list, and a four-year bet on your future self.',
            tone: 'neutral',
            effects: [
              { k: 'education', op: 'enroll', programId: 'edu.bachelor.general' },
              { k: 'career', op: 'setStatus', status: 'student' },
              { k: 'flag', flag: 'chosePath', value: true },
              { k: 'memory', title: 'Started university', tone: 'milestone', category: 'education', weight: 7 },
            ],
          },
        ],
      },
      {
        id: 'vocational',
        label: 'Train for a trade',
        detail: 'Two years, low cost, earning sooner than everyone else.',
        hint: 'safe',
        tags: ['education', 'pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Started training',
            narrative: 'Tools, a mentor who shouts, and a payslip within eighteen months.',
            tone: 'positive',
            effects: [
              { k: 'education', op: 'enroll', programId: 'edu.vocational' },
              { k: 'career', op: 'setStatus', status: 'student' },
              { k: 'flag', flag: 'chosePath', value: true },
              { k: 'memory', title: 'Started vocational training', tone: 'milestone', category: 'education', weight: 6 },
            ],
          },
        ],
      },
      {
        id: 'work',
        label: 'Start working now',
        detail: 'No debt, no credential, immediate income.',
        hint: 'mixed',
        tags: ['pragmatic', 'independence'],
        outcomes: [
          {
            weight: 1,
            label: 'Went to work',
            narrative: 'While your friends took loans, you took a job. For five years you will be ahead.',
            tone: 'neutral',
            effects: [
              { k: 'flag', flag: 'chosePath', value: true },
              { k: 'skill', skill: 'selfManagement', delta: 6 },
              { k: 'triggerEvent', eventId: 'career.firstJob' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'education.graduating',
    title: 'It is nearly over',
    description: 'The final year. Recruiters are circling, and so is the repayment schedule.',
    category: 'education',
    pools: ['pool.education'],
    weight: 9,
    requires: [{ k: 'careerStatus', anyOf: ['student'] }],
    cooldown: 3,
    choices: [
      {
        id: 'finish',
        label: 'Finish properly',
        hint: 'safe',
        tags: ['discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Pushed through',
            narrative: 'You did the work. It is unglamorous and it counts.',
            tone: 'positive',
            effects: [
              { k: 'skill', skill: 'selfManagement', delta: 5 },
              { k: 'habit', habit: 'learning', delta: 6 },
              { k: 'stat', stat: 'stress', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'coast',
        label: 'Do the minimum and start job hunting',
        hint: 'mixed',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Split the difference',
            narrative: 'Grades slipped, offers arrived. Almost nobody ever asked about the grades.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'adjust', field: 'performance', delta: 4 },
              { k: 'skill', skill: 'social', delta: 4 },
              { k: 'stat', stat: 'stress', delta: -4 },
            ],
          },
        ],
      },
      {
        id: 'dropout',
        label: 'Leave without finishing',
        detail: 'Cut the losses. Keep the debt.',
        hint: 'risky',
        tags: ['risk'],
        outcomes: [
          {
            weight: 1,
            label: 'Dropped out',
            narrative: 'You kept the loan and lost the letters after your name.',
            tone: 'negative',
            effects: [
              { k: 'education', op: 'dropOut' },
              { k: 'career', op: 'setStatus', status: 'unemployed' },
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'regret', op: 'add', id: 'regret.dropout', label: 'Left study unfinished', weight: 14 },
              { k: 'triggerEvent', eventId: 'career.firstJob' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'education.returnToStudy',
    title: 'Back to school?',
    description:
      'A qualification would open the next tier. It would also cost evenings, money, and a degree of humility.',
    category: 'education',
    pools: ['pool.education'],
    weight: 6,
    cooldown: 6,
    requires: [
      { k: 'careerStatus', anyOf: ['employed', 'selfEmployed', 'unemployed', 'break'] },
      { k: 'age', min: 23, max: 55 },
    ],
    weightMods: [
      { conditions: [{ k: 'habit', habit: 'learning', op: '>=', value: 55 }], multiplier: 1.6 },
      { conditions: [{ k: 'num', path: 'roleLevel', op: '<=', value: 1 }], multiplier: 1.4 },
    ],
    choices: [
      {
        id: 'masters',
        label: 'Take a master’s degree',
        hint: 'costly',
        requires: [{ k: 'educationAtLeast', level: 'bachelor' }],
        showWhenLocked: true,
        tags: ['education', 'growth'],
        outcomes: [
          {
            weight: 1,
            label: 'Enrolled part-time',
            narrative: 'Two years of lectures after work. You will not remember the middle of it.',
            tone: 'neutral',
            effects: [
              { k: 'education', op: 'enroll', programId: 'edu.master' },
              { k: 'stat', stat: 'stress', delta: 10 },
              { k: 'habit', habit: 'learning', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'cert',
        label: 'Take a certification instead',
        detail: 'Cheaper, faster, narrower.',
        hint: 'safe',
        tags: ['education', 'pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Started a certification',
            narrative: 'A pragmatic credential. Your employer paid for half of it.',
            tone: 'positive',
            effects: [
              { k: 'education', op: 'enroll', programId: 'edu.cert.pm' },
              { k: 'habit', habit: 'learning', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'self',
        label: 'Teach yourself',
        detail: 'Free. Entirely dependent on your own discipline.',
        hint: 'mixed',
        tags: ['education', 'discipline'],
        outcomes: [
          {
            weight: 55,
            label: 'It stuck',
            narrative: 'You actually did it — an hour most evenings for a year.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'trait', trait: 'discipline', op: '>=', value: 62 }], multiplier: 2.2 },
              { conditions: [{ k: 'habit', habit: 'procrastination', op: '<=', value: 35 }], multiplier: 1.6 },
            ],
            effects: [
              { k: 'skill', skill: 'technical', delta: 9 },
              { k: 'habit', habit: 'learning', delta: 10 },
              { k: 'stat', stat: 'purpose', delta: 5 },
            ],
          },
          {
            weight: 45,
            label: 'It faded',
            narrative: 'Three weeks of enthusiasm and a bookmark folder you stopped opening.',
            tone: 'negative',
            effects: [
              { k: 'skill', skill: 'technical', delta: 2 },
              { k: 'habit', habit: 'procrastination', delta: 5 },
              { k: 'inertia', op: 'pressure', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'no',
        label: 'Not now',
        hint: 'safe',
        defers: { id: 'int.study', label: 'Go back and study', topic: 'education', urgency: 18 },
        outcomes: [
          { weight: 1, label: 'Deferred', narrative: 'Next year, probably.', tone: 'neutral', effects: [{ k: 'inertia', op: 'pressure', delta: 3 }] },
        ],
      },
    ],
  },
  {
    id: 'education.expensiveMistake',
    title: 'This was the wrong course',
    description:
      'Two terms in, it is obvious this is not what you wanted. The fees are non-refundable.',
    category: 'education',
    pools: ['pool.education'],
    weight: 4,
    requires: [{ k: 'careerStatus', anyOf: ['student'] }],
    cooldown: 20,
    choices: [
      {
        id: 'switch',
        label: 'Switch, and lose a year',
        hint: 'costly',
        tags: ['courage'],
        outcomes: [
          {
            weight: 1,
            label: 'Started again',
            narrative: 'Expensive, embarrassing, correct.',
            tone: 'neutral',
            effects: [
              { k: 'cashIncomeMonths', months: -3, note: 'lost tuition' },
              { k: 'stat', stat: 'purpose', delta: 9 },
              { k: 'trait', trait: 'adaptability', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'finish',
        label: 'Finish it anyway',
        hint: 'mixed',
        tags: ['discipline', 'sunk-cost'],
        outcomes: [
          {
            weight: 1,
            label: 'Finished what you started',
            narrative: 'You got the credential for a field you never entered.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'purpose', delta: -6 },
              { k: 'skill', skill: 'selfManagement', delta: 6 },
              { k: 'regret', op: 'add', id: 'regret.wrongCourse', label: 'Studied the wrong thing', weight: 10 },
            ],
          },
        ],
      },
    ],
  },
];
